import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Grid, TextField, Button,
  Card, CardContent, IconButton, List, ListItem,
  ListItemText, ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Box,
  InputAdornment,
  Chip,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Delete, DragIndicator, Edit } from '@mui/icons-material';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import {
  getAllCategorie, createCategoria,
  updateCategoria, deleteCategoria, deleteCategorie, reorderCategorie
} from '../APIs/CategoriesAPI'
import {
  getAllSottocategorie, createSottocategoria,
  updateSottocategoria, deleteSottocategoria
} from '../APIs/SubCategoriesAPI.js'
import SidebarLayout from '../components/sidebarLayout';
import { SetupWSEventListener } from '../utils/setupWSEventListener.js';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from "../contexts/SnackBarContext";

const parseVatRate = (value) => {
  if (String(value).trim() === '') return { valid: true, value: null };

  const parsed = Number(String(value).replace(',', '.'));
  return {
    valid: Number.isFinite(parsed) && parsed >= 0 && parsed <= 100,
    value: parsed,
  };
};

const mapCategories = (categories, subcategories) => categories.map(cat => ({
  id: cat.id,
  name: cat.name,
  description: cat.description || '',
  visible: cat.visible,
  vatRate: cat.vatRate ?? null,
  sortOrder: cat.sortOrder,
  subcategories: subcategories
    .filter(sub => sub.categoryId === cat.id)
    .map(sub => sub.name),
}));

const SortableCategory = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ position: 'relative', zIndex: isDragging ? 2 : 'auto' }}
    >
      {children({ attributes, listeners })}
    </Box>
  );
};

const GestioneCategorie = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [categories, setCategories] = useState([]);
  const [newSubcategory, setNewSubcategory] = useState('');
  const [selectedCatId, setSelectedCatId] = useState(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visible, setVisible] = useState(true);
  const [vatRate, setVatRate] = useState('');

  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryData, setEditingCategoryData] = useState({
    name: '',
    description: '',
    visible: true,
    vatRate: '',
  });
  const [editingSub, setEditingSub] = useState({ catId: null, index: null });
  const [editingValue, setEditingValue] = useState('');

  const [search, setSearch] = useState();

  const [errorFlag, setErrorFlag] = useState(false)
  const [isReordering, setIsReordering] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeletingCategories, setBulkDeletingCategories] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleAlert = useCallback((msg, type) => {
    showSnackbar(msg, type);
  }, [showSnackbar]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    try {
      const [categorieDB, sottocategorieDB] = await Promise.all([
        getAllCategorie(token, tenant, { includeInactive: true }),
        getAllSottocategorie(token, tenant)
      ]);

      setCategories(mapCategories(categorieDB, sottocategorieDB));
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorRecoveryCat") + error.message, "error")
    }
  }, [handleAlert, t]);

  useEffect(() => {
    fetchData();

    const cleanup = SetupWSEventListener(fetchData);
    return cleanup;
  }, [fetchData]);

  const categorieFiltrate = useMemo(() => {
    if (!search || search.length < 1) return categories
    return categories.filter(cat =>
      cat.name?.toLowerCase().includes(search?.toLowerCase())
      || cat.subcategories.some(sub => { return sub?.toLowerCase().includes(search?.toLowerCase()) }))
  }, [categories, search])

  const visibleCategoryIds = useMemo(() => categorieFiltrate.map(({ id }) => Number(id)), [categorieFiltrate]);
  const allVisibleCategoriesSelected = visibleCategoryIds.length > 0
    && visibleCategoryIds.every((id) => selectedCategoryIds.has(id));
  const someVisibleCategoriesSelected = visibleCategoryIds.some((id) => selectedCategoryIds.has(id));

  const toggleCategorySelection = (categoryId) => {
    const normalizedId = Number(categoryId);
    setSelectedCategoryIds((previous) => {
      const next = new Set(previous);
      if (next.has(normalizedId)) next.delete(normalizedId);
      else next.add(normalizedId);
      return next;
    });
  };

  const toggleVisibleCategories = () => {
    setSelectedCategoryIds((previous) => {
      const next = new Set(previous);
      visibleCategoryIds.forEach((id) => {
        if (allVisibleCategoriesSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const saveCategoryOrder = async (nextCategories, previousCategories) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    setCategories(nextCategories);
    setIsReordering(true);
    try {
      await reorderCategorie(nextCategories.map(({ id }) => id), token, tenant);
    } catch (error) {
      setCategories(previousCategories);
      handleAlert(
        `${t('manage_categories.order_error', { defaultValue: 'Impossibile salvare l\'ordine delle categorie: ' })}${error.message}`,
        'error',
      );
    } finally {
      setIsReordering(false);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || search || isReordering) return;
    const oldIndex = categories.findIndex(({ id }) => id === active.id);
    const newIndex = categories.findIndex(({ id }) => id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveCategoryOrder(arrayMove(categories, oldIndex, newIndex), categories);
  };

  const handleMoveCategory = (categoryId, direction) => {
    if (search || isReordering) return;
    const oldIndex = categories.findIndex(({ id }) => id === categoryId);
    const newIndex = oldIndex + direction;
    if (oldIndex < 0 || newIndex < 0 || newIndex >= categories.length) return;
    saveCategoryOrder(arrayMove(categories, oldIndex, newIndex), categories);
  };

  const handleAddCategory = async () => {
    const trimmedName = name.trim();
    if (trimmedName === '') {
      // alert('Il nome non può essere vuoto');
      showError()
      setErrorFlag(true)
      return;
    }

    const parsedVatRate = parseVatRate(vatRate);
    if (!parsedVatRate.valid) {
      handleAlert(t("manage_categories.vat_invalid"), "warning");
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const categoria = {
      name: trimmedName,
      description,
      visible,
      ...(parsedVatRate.value !== null ? { vatRate: parsedVatRate.value } : {}),
    };

    try {
      await createCategoria(categoria, token, tenant);
      const [categorieDB, sottocategorieDB] = await Promise.all([
        getAllCategorie(token, tenant, { includeInactive: true }),
        getAllSottocategorie(token, tenant),
      ]);

      setCategories(mapCategories(categorieDB, sottocategorieDB));
      setName('');
      setDescription('');
      setVisible(true);
      setVatRate('');
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorAddCategory") + error.message, "error")
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategory.trim() || selectedCatId === null) return;
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const sottocategoria = {
      name: newSubcategory.trim(),
      categoryId: selectedCatId,
    };
    try {
      await createSottocategoria(sottocategoria, token, tenant);
      const allSubs = await getAllSottocategorie(token, tenant);
      setCategories(prev =>
        prev.map(cat =>
          cat.id === selectedCatId
            ? {
              ...cat,
              subcategories: allSubs
                .filter(sub => sub.categoryId === cat.id)
                .map(sub => sub.name),
            }
            : cat
        )
      );
      setNewSubcategory('');
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorAddSubCategory") + error.message, "error")
    }
  };

  const handleUpdateCategory = async (id) => {
    const trimmedName = editingCategoryData.name.trim();
    if (trimmedName === '') {
      // alert('Il nome non può essere vuoto');
      showError()
      setErrorFlag(true)
      return;
    }

    const parsedVatRate = parseVatRate(editingCategoryData.vatRate);
    if (!parsedVatRate.valid) {
      handleAlert(t("manage_categories.vat_invalid"), "warning");
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const dataToUpdate = {
      ...editingCategoryData,
      name: trimmedName,
      vatRate: parsedVatRate.value,
    };
    try {
      await updateCategoria(id, dataToUpdate, token, tenant);
      const [categorieDB, sottocategorieDB] = await Promise.all([
        getAllCategorie(token, tenant, { includeInactive: true }),
        getAllSottocategorie(token, tenant),
      ]);
      setCategories(mapCategories(categorieDB, sottocategorieDB));
      setEditingCategoryId(null);
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorUpdateCat") + error.message, "error")
    }
  };


  const handleUpdateSubcategory = async (catId, subIndex) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const category = categories.find(cat => cat.id === catId);
    const oldSubName = category.subcategories[subIndex];

    try {
      const allSubs = await getAllSottocategorie(token, tenant);
      const subToUpdate = allSubs.find(
        sub => sub.name === oldSubName && sub.categoryId === catId
      );

      if (!subToUpdate) throw new Error("Sottocategoria non trovata");

      await updateSottocategoria(
        subToUpdate.id, // ID corretto
        {
          name: editingValue,
          categoryId: catId,
        },
        token,
        tenant
      );

      // Aggiorna stato locale
      setCategories(prev =>
        prev.map(cat => {
          if (cat.id !== catId) return cat;
          const updatedSubcategories = cat.subcategories.map((sub, i) =>
            i === subIndex ? editingValue : sub
          );
          return { ...cat, subcategories: updatedSubcategories };
        })
      );

      setEditingSub({ catId: null, index: null });
      setEditingValue('');
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorUpdateSubCat") + error.message, "error")
    }
  };


  const handleDeleteCategory = async (id) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    try {
      await deleteCategoria(id, token, tenant);
      setCategories(prev => prev.filter(cat => cat.id !== id));
      setSelectedCategoryIds((previous) => {
        const next = new Set(previous);
        next.delete(Number(id));
        return next;
      });
      if (selectedCatId === id) setSelectedCatId(null);
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorDeleteCat") + error.message, "error")
    }
  };

  const handleBulkDeleteCategories = async () => {
    if (selectedCategoryIds.size === 0) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const categoryIds = Array.from(selectedCategoryIds);
    setBulkDeletingCategories(true);
    try {
      const result = await deleteCategorie(categoryIds, token, tenant);
      setCategories((previous) => previous.filter(({ id }) => !selectedCategoryIds.has(Number(id))));
      setSelectedCategoryIds(new Set());
      setSelectedCatId((current) => categoryIds.includes(Number(current)) ? null : current);
      setBulkDeleteDialogOpen(false);
      handleAlert(t('bulkDelete.categoriesDeleted', { count: result.deletedCount || categoryIds.length }), 'success');
    } catch (error) {
      handleAlert(error?.message || t('bulkDelete.deleteCategoriesError'), 'error');
    } finally {
      setBulkDeletingCategories(false);
    }
  };

  const handleDeleteSubcategory = async (catId, subIndex) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const subName = categories.find(cat => cat.id === catId)?.subcategories[subIndex];
    try {
      const allSubs = await getAllSottocategorie(token, tenant);
      const subToDelete = allSubs.find(sub => sub.name === subName && sub.categoryId === catId);
      if (subToDelete) {
        await deleteSottocategoria(subToDelete.id, token, tenant);
        setCategories(prev =>
          prev.map(cat =>
            cat.id === catId
              ? {
                ...cat,
                subcategories: cat.subcategories.filter((_, i) => i !== subIndex),
              }
              : cat
          )
        );
      }
    } catch (error) {
      handleAlert(t("handleCategory_alert.errorDeleteSubCat") + error.message, "error")
    }
  };

  function showError() {
    if (errorFlag) {
      return <Typography>
        {t('blankname')}
      </Typography>;
    }


  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 } }}>
        <Typography variant="h5" gutterBottom>
          {t('manage_categories.title')}
        </Typography>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("client_page.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("description")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t("manage_categories.vat_rate")}
                  type="number"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  helperText={t("manage_categories.vat_default_help")}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={visible}
                      onChange={(e) => setVisible(e.target.checked)}
                    />
                  }
                label={visible ? 'Categoria attiva' : 'Categoria disattivata'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box width={"100%"}>
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Button fullWidth variant="contained" onClick={handleAddCategory}>
                      {t('manage_categories.add_category')}
                    </Button>
                  </Box>
                  <Box>{showError()}</Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); }}
                  fullWidth
                  placeholder={t("search")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('manage_categories.order_help', {
            defaultValue: 'Trascina le categorie oppure usa le frecce per scegliere l\'ordine mostrato nella comanda.',
          })}
        </Typography>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            onClick={toggleVisibleCategories}
            disabled={visibleCategoryIds.length === 0}
            startIcon={(
              <Checkbox
                checked={allVisibleCategoriesSelected}
                indeterminate={!allVisibleCategoriesSelected && someVisibleCategoriesSelected}
              />
            )}
          >
            {allVisibleCategoriesSelected ? t('bulkDelete.deselectVisible') : t('bulkDelete.selectVisible')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {t('bulkDelete.selectedCount', { count: selectedCategoryIds.size })}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              disabled={selectedCategoryIds.size === 0}
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              {t('bulkDelete.deleteSelected')}
            </Button>
          </Box>
        </Box>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={categorieFiltrate.map(({ id }) => id)}
            strategy={verticalListSortingStrategy}
          >
          {categorieFiltrate.map((cat) => {
            const categoryIndex = categories.findIndex(({ id }) => id === cat.id);
            const orderDisabled = Boolean(search) || isReordering;
            return (
          <SortableCategory key={cat.id} id={cat.id} disabled={orderDisabled}>
            {({ attributes, listeners }) => (
          <Card sx={{ mb: 2, opacity: isReordering ? 0.8 : 1 }}>
            <CardContent>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                  <Checkbox
                    checked={selectedCategoryIds.has(Number(cat.id))}
                    onChange={() => toggleCategorySelection(cat.id)}
                    inputProps={{ 'aria-label': `Seleziona ${cat.name}` }}
                  />
                  {editingCategoryId === cat.id ? (
                    <>
                      <TextField
                        value={editingCategoryData.name}
                        onChange={(e) => setEditingCategoryData(prev => ({ ...prev, name: e.target.value }))}
                        size="small"
                        sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, width: { xs: "100%", sm: 200 } }}
                      />
                      <TextField
                        value={editingCategoryData.description}
                        onChange={(e) => setEditingCategoryData(prev => ({ ...prev, description: e.target.value }))}
                        size="small"
                        sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, width: { xs: "100%", sm: 200 } }}
                      />
                      <TextField
                        value={editingCategoryData.vatRate}
                        onChange={(e) => setEditingCategoryData(prev => ({ ...prev, vatRate: e.target.value }))}
                        label={t("manage_categories.vat_rate")}
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, width: { xs: "100%", sm: 130 } }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingCategoryData.visible}
                            onChange={(e) =>
                              setEditingCategoryData(prev => ({ ...prev, visible: e.target.checked }))
                            }
                          />
                        }
                        label={editingCategoryData.visible ? 'Categoria attiva' : 'Categoria disattivata'}
                      />
                      <Button onClick={() => handleUpdateCategory(cat.id)} sx={{ mr: 1 }}>{t('save')}</Button>
                      <Button onClick={() => setEditingCategoryId(null)}>{t('cancel')}</Button>
                    </>
                  ) : (
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: cat.visible ? 'green' : 'red',
                          display: 'inline-block',
                          mr: 1,
                        }}
                      />
                      {cat.name}
                      {!cat.visible && (
                        <Chip size="small" color="error" variant="outlined" label="Disattivata" sx={{ ml: 1 }} />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          cat.vatRate === null
                            ? t("manage_categories.vat_default")
                            : `${t("manage_categories.vat_short")} ${cat.vatRate}%`
                        }
                        sx={{ ml: 1 }}
                      />
                      <IconButton
                        onClick={() => {
                          setEditingCategoryId(cat.id);
                          setEditingCategoryData({
                            name: cat.name,
                            description: cat.description || '',
                            visible: cat.visible,
                            vatRate: cat.vatRate ?? '',
                          });
                        }}
                        sx={{ ml: 1 }}
                      >
                        <Edit />
                      </IconButton>
                    </Typography>
                  )}
                </Grid>
                <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={t('manage_categories.drag', { defaultValue: 'Trascina per spostare' })}>
                    <span>
                      <IconButton
                        aria-label={t('manage_categories.drag', { defaultValue: 'Trascina per spostare' })}
                        disabled={orderDisabled}
                        {...attributes}
                        {...listeners}
                        sx={{ cursor: orderDisabled ? 'default' : 'grab', touchAction: 'none' }}
                      >
                        <DragIndicator />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('manage_categories.move_up', { defaultValue: 'Sposta su' })}>
                    <span>
                      <IconButton
                        aria-label={t('manage_categories.move_up', { defaultValue: 'Sposta su' })}
                        disabled={orderDisabled || categoryIndex === 0}
                        onClick={() => handleMoveCategory(cat.id, -1)}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('manage_categories.move_down', { defaultValue: 'Sposta giu' })}>
                    <span>
                      <IconButton
                        aria-label={t('manage_categories.move_down', { defaultValue: 'Sposta giu' })}
                        disabled={orderDisabled || categoryIndex === categories.length - 1}
                        onClick={() => handleMoveCategory(cat.id, 1)}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton onClick={() => handleDeleteCategory(cat.id)}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>

              <List dense>
                {cat.subcategories.map((sub, index) => (
                  <ListItem key={index} sx={{ pl: 3 }}>
                    {editingSub.catId === cat.id && editingSub.index === index ? (
                      <TextField
                        size="small"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => handleUpdateSubcategory(cat.id, index)}
                        autoFocus
                      />
                    ) : (
                      <ListItemText primary={sub} />
                    )}
                    <ListItemSecondaryAction>
                      {editingSub.catId === cat.id && editingSub.index === index ? null : (
                        <IconButton
                          onClick={() => {
                            setEditingSub({ catId: cat.id, index });
                            setEditingValue(sub);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton onClick={() => handleDeleteSubcategory(cat.id, index)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('manage_categories.add_subcategory')}
                    value={selectedCatId === cat.id ? newSubcategory : ''}
                    onChange={(e) => {
                      setSelectedCatId(cat.id);
                      setNewSubcategory(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth variant="outlined" onClick={handleAddSubcategory}>
                    {t('manage_categories.add_button')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
            )}
          </SortableCategory>
            );
          })}
          </SortableContext>
        </DndContext>

        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={() => {
            if (!bulkDeletingCategories) setBulkDeleteDialogOpen(false);
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{t('bulkDelete.categoriesTitle')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('bulkDelete.categoriesConfirm', { count: selectedCategoryIds.size })}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeletingCategories}>
              {t('cancel_button')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDeleteCategories}
              disabled={bulkDeletingCategories}
              startIcon={bulkDeletingCategories ? <CircularProgress size={16} /> : <Delete />}
            >
              {bulkDeletingCategories ? t('bulkDelete.deleting') : t('bulkDelete.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
};

export default GestioneCategorie;
